import React, { useState } from "react";
import { MapPin, ShieldCheck, User2, Pencil, Lock } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { formatRole } from "@/helpers";
import { Role } from "@/types/roles";
import { Button, Input, useToast } from "@/components/ui";
import Modal from "@/components/ui/Modal";
import { useFormik } from "formik";
import * as Yup from "yup";

const cls = (...c: (string | false | undefined)[]) =>
  c.filter(Boolean).join(" ");

const getInitials = (first = "", last = "") =>
  `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();

const SectionCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  right?: React.ReactNode;
}> = ({ title, icon, children, right }) => (
  <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <div className="p-2 rounded-xl bg-gray-100">{icon}</div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      {right}
    </div>
    {children}
  </div>
);

const editSchema = Yup.object({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  mobile: Yup.string().matches(/^[0-9]*$/, "Mobile must contain only digits"),
  address: Yup.string(),
  city: Yup.string(),
  state: Yup.string(),
  country: Yup.string(),
  pincode: Yup.string(),
});

const passwordSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Please confirm your new password"),
});

export default function ProfilePage() {
  const { user, updateProfile, changePassword } = useAuth();
  const { success, error } = useToast();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);

  if (!user) {
    return <div className="p-6 text-gray-500">No user data available.</div>;
  }

  const initials = getInitials(user.firstName, user.lastName);

  const editFormik = useFormik({
    initialValues: {
      first_name: user.firstName ?? "",
      last_name: user.lastName ?? "",
      mobile: user.mobile ?? "",
      address: user.address ?? "",
      city: user.city ?? "",
      state: user.state ?? "",
      country: user.country ?? "",
      pincode: user.pincode ?? "",
    },
    validationSchema: editSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await updateProfile(values);
        success("Profile updated successfully");
        setIsEditOpen(false);
      } catch (err: any) {
        error(err?.message || "Failed to update profile");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const passwordFormik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: passwordSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await changePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        });
        success("Password changed successfully");
        resetForm();
        setIsPasswordOpen(false);
      } catch (err: any) {
        error(err?.message || "Failed to change password");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-slate-50 to-white">
      {/* Banner */}
      <div className="relative">
        <div className="h-36 bg-gradient-to-r from-[#F8AD4D] to-[rgb(59_130_246_/_0.5)]" />
        <div className="max-w-5xl mx-auto px-4 md:px-6 -mt-16 pb-8">
          <div className="bg-white rounded-3xl shadow-xl border border-white/80 p-4 sm:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 text-white flex items-center justify-center text-2xl font-bold shadow-lg">
                {initials}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {user.firstName} {user.lastName}
                </h1>
                <div className="mt-1 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    {formatRole(user.role as Role)}
                  </span>
                  <span
                    className={cls(
                      "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
                      user.status === "active"
                        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                        : "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
                    )}
                  >
                    {user.status === "active" ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <Button
                onClick={() => setIsEditOpen(true)}
                className="bg-primary-button hover:bg-primary-button text-white px-4 py-2 rounded-lg flex items-center gap-2 font-bold"
              >
                <Pencil className="w-4 h-4" /> Edit Profile
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsPasswordOpen(true)}
                className="flex items-center gap-2 font-semibold"
              >
                <Lock className="w-4 h-4" /> Change Password
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard
          title="Account Info"
          icon={<User2 className="w-5 h-5 text-gray-700" />}
        >
          <div className="space-y-3 text-sm">
            {[
              { label: "Username", value: user.username },
              { label: "Email", value: user.email },
              { label: "Role", value: formatRole(user.role as Role) },
              { label: "Status", value: user.status },
            ].map(({ label, value }) => (
              <div
                key={label}
                className="flex justify-between py-1 border-b border-gray-50 last:border-0"
              >
                <span className="text-gray-500">{label}</span>
                <span className="font-medium text-gray-900">
                  {value || "—"}
                </span>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard
          title="Contact & Address"
          icon={<MapPin className="w-5 h-5 text-gray-700" />}
        >
          <div className="space-y-3 text-sm">
            {[
              { label: "Mobile", value: user.mobile },
              { label: "Address", value: user.address },
              { label: "City", value: user.city },
              { label: "State", value: user.state },
              { label: "Country", value: user.country },
              { label: "Pincode", value: user.pincode },
            ].map(({ label, value }) => (
              <div
                key={label}
                className="flex justify-between py-1 border-b border-gray-50 last:border-0"
              >
                <span className="text-gray-500">{label}</span>
                <span className="font-medium text-gray-900">
                  {value || "—"}
                </span>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Edit Profile Modal */}
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Profile"
        size="md"
        footer={
          <div className="flex justify-end gap-3 px-2">
            <Button
              type="submit"
              form="edit-profile-form"
              disabled={editFormik.isSubmitting}
              className="bg-primary-button hover:bg-primary-button text-white px-4 py-2 rounded-lg font-bold"
            >
              {editFormik.isSubmitting ? "Saving..." : "Save"}
            </Button>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>
              Cancel
            </Button>
          </div>
        }
      >
        <form
          id="edit-profile-form"
          onSubmit={editFormik.handleSubmit}
          className="px-4 py-4 space-y-4"
        >
          {[
            { name: "first_name", placeholder: "First Name" },
            { name: "last_name", placeholder: "Last Name" },
            { name: "mobile", placeholder: "Mobile Number" },
            { name: "address", placeholder: "Address" },
            { name: "city", placeholder: "City" },
            { name: "state", placeholder: "State" },
            { name: "country", placeholder: "Country" },
            { name: "pincode", placeholder: "Pincode" },
          ].map(({ name, placeholder }) => (
            <div key={name}>
              <Input
                name={name}
                placeholder={placeholder}
                onChange={editFormik.handleChange}
                onBlur={editFormik.handleBlur}
                value={(editFormik.values as any)[name]}
              />
              {(editFormik.touched as any)[name] &&
                (editFormik.errors as any)[name] && (
                  <p className="text-red-500 text-sm mt-1">
                    {(editFormik.errors as any)[name]}
                  </p>
                )}
            </div>
          ))}
        </form>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        isOpen={isPasswordOpen}
        onClose={() => setIsPasswordOpen(false)}
        title="Change Password"
        size="sm"
        footer={
          <div className="flex justify-end gap-3 px-2">
            <Button
              type="submit"
              form="change-password-form"
              disabled={passwordFormik.isSubmitting}
              className="bg-primary-button hover:bg-primary-button text-white px-4 py-2 rounded-lg font-bold"
            >
              {passwordFormik.isSubmitting ? "Saving..." : "Update Password"}
            </Button>
            <Button variant="outline" onClick={() => setIsPasswordOpen(false)}>
              Cancel
            </Button>
          </div>
        }
      >
        <form
          id="change-password-form"
          onSubmit={passwordFormik.handleSubmit}
          className="px-4 py-4 space-y-4"
        >
          {[
            { name: "currentPassword", placeholder: "Current Password" },
            { name: "newPassword", placeholder: "New Password" },
            { name: "confirmPassword", placeholder: "Confirm New Password" },
          ].map(({ name, placeholder }) => (
            <div key={name}>
              <Input
                name={name}
                type="password"
                placeholder={placeholder}
                onChange={passwordFormik.handleChange}
                onBlur={passwordFormik.handleBlur}
                value={(passwordFormik.values as any)[name]}
              />
              {(passwordFormik.touched as any)[name] &&
                (passwordFormik.errors as any)[name] && (
                  <p className="text-red-500 text-sm mt-1">
                    {(passwordFormik.errors as any)[name]}
                  </p>
                )}
            </div>
          ))}
        </form>
      </Modal>
    </div>
  );
}
